/* eslint-disable complexity */
import { Suspense, useEffect } from 'react';

import { useUFOComponentExperience } from '@atlassian/ufo';

import { FeatureFlags } from '@/components/signup/bxpkit-signup/src/model/devops';
import { LayoutType } from '@/components/signup/bxpkit-signup/src/sign-up-layout-container';
import { ufoEventPaidSignupLandingPageLoad } from '@/components/signup/utils/ufo-events';
import { useDeveloperBundleIntent } from '@/components/user-context/helpers/cohorts';
import { useProductIntent } from '@/components/user-context/helpers/use-product-intent';
import { useUserContext } from '@/components/user-context/user-context-provider';
import {
	AA_AUTOCOMPLETE_POTENTIAL_CONVERSION_EVENT_LOCAL_STORAGE_KEY,
	sendConversionEventAlgolia,
} from '@/patterns/global-nav/src/auto-complete-helpers';
import { defaultFeatureFlags } from '@/templates/try/cloud/signup/signup-page/src/utils/data-mappings';
import { getQueryParams } from '@/templates/try/cloud/signup/signup-page/src/utils/query-param-resolver';
import { getLocalStorage } from '@/utils/localstorage';

import { useLocation } from '../../../../../../utils/location-provider';

import { ErrorLocale } from './components/error-locale/error-locale';
import { SignupDefault } from './components/signup-default';
import { DeveloperBundleSignup } from './developer/developer-bundle';
import { useProductSignupComponent } from './hooks/use-product-signup-component';
import { handleSignupRouteRedirects } from './utils/signup-redirection-resolver';

type SignupPageTemplateProps = {
	layout?: LayoutType;
	featureFlags?: FeatureFlags;
};
export type SignupPageTemplateBaseProps = {
	bundle?: string;
	migrationSourceUuid?: string;
	edition?: string;
	layout?: LayoutType;
	featureFlags?: FeatureFlags;
};

export const SignupPageTemplate = ({ featureFlags }: SignupPageTemplateProps) => {
	const location = useLocation();
	const { bundle: useProductIntentBundle } = useProductIntent(location.href);

	const { product, bundle, edition, migrationSourceUuid, layout } = getQueryParams(location.search);

	if (
		handleSignupRouteRedirects({
			product: product as string,
			bundle,
		})
	) {
		return <></>;
	}

	return (
		<SignupPageTemplateBase
			bundle={useProductIntentBundle}
			edition={edition}
			layout={layout}
			featureFlags={{
				...defaultFeatureFlags,
				...featureFlags,
			}}
			migrationSourceUuid={migrationSourceUuid}
		/>
	);
};

export const SignupPageTemplateBase = ({
	bundle,
	edition,
	layout,
	featureFlags,
	migrationSourceUuid,
}: SignupPageTemplateBaseProps) => {
	useUFOComponentExperience(ufoEventPaidSignupLandingPageLoad);

	return (
		<Suspense>
			<div>
				<SignUpComponent
					bundleOverride={bundle}
					editionOverride={edition}
					layout={layout}
					featureFlags={{
						...defaultFeatureFlags,
						...featureFlags,
					}}
					migrationSourceUuid={migrationSourceUuid}
				/>
			</div>
		</Suspense>
	);
};

// TODO: Temporary fix for hot-120109
const SignUpComponent = ({
	layout = 'first-impressions',
	migrationSourceUuid,
}: {
	bundleOverride?: string;
	editionOverride?: string;
	migrationSourceUuid?: string;
	layout?: LayoutType;
	isN2NParam?: boolean;
	featureFlags: FeatureFlags;
	// eslint-disable-next-line complexity
}) => {
	const { user: userProfile } = useUserContext();
	const isDeveloperBundle = useDeveloperBundleIntent();

	const ProductSignupComponent = useProductSignupComponent();

	const countryCode =
		// @ts-ignore
		typeof window !== 'undefined' ? window.countryCode : undefined;
	const blockedLocales = ['RU', 'BY'];

	useEffect(() => {
		const getaaCompleteQueryObj = getLocalStorage(
			AA_AUTOCOMPLETE_POTENTIAL_CONVERSION_EVENT_LOCAL_STORAGE_KEY,
		);
		if (getaaCompleteQueryObj) {
			const parsedStringifyAttributes = JSON.parse(getaaCompleteQueryObj);
			sendConversionEventAlgolia({
				objectID: parsedStringifyAttributes.objectID,
				queryID: parsedStringifyAttributes.queryID,
				position: parsedStringifyAttributes.position,
			});
		}
	}, []);

	if (countryCode && blockedLocales.includes(countryCode)) {
		return <ErrorLocale locale={countryCode} />;
	}

	if (userProfile.requestInProgress) {
		return <></>;
	}

	if (isDeveloperBundle) {
		return <DeveloperBundleSignup />;
	}

	// Render product-specific signup component if conditions are met
	if (layout !== 'standalone' && !migrationSourceUuid && ProductSignupComponent) {
		return <ProductSignupComponent />;
	}

	return <SignupDefault />;
};
